import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    TextField,
    LinearProgress,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Skeleton,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Button,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { useQuestionAwnserController } from "./context/question-awnser-controller";
import { useQuestionAwnserState } from "./context/question-awnser-context";
import { IListQuestionAnwserRequest } from "../../model/base/request/list-question-anwser-request";
import PageHeader from "../components/shared/page-header";

export function QuestionAwnserComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = useQuestionAwnserController();
    const { questionAwnser, categories } = useQuestionAwnserState();
    const [paramsRequet, setParamsRequest] = useState<IListQuestionAnwserRequest>({ page: 0, pageSize: 10 } as IListQuestionAnwserRequest);
    const [expanded, setExpanded] = React.useState<string | false>(false);

    const handleChange =
        (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequet);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequet]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 10 } as IListQuestionAnwserRequest);
    };

    return (
        <Box>
            <PageHeader
                title="Perguntas e Respostas Frequentes"
                subtitle="Encontre respostas para as dúvidas mais comuns"
                breadcrumbs={[{ label: 'Perguntas e Respostas' }]}
            />

            {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={5}>
                                <TextField
                                    fullWidth
                                    label="Buscar por pergunta"
                                    placeholder="Digite para buscar"
                                    value={paramsRequet.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequet, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        value={paramsRequet.categoryId || ''}
                                        label="Categoria"
                                        onChange={(e) =>
                                            setParamsRequest({
                                                ...paramsRequet,
                                                page: 0,
                                                categoryId: e.target.value as number,
                                            })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {categories.map((element) => (
                                            <MenuItem value={element.id} key={element.id}>
                                                {element.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                        {loadMoreData && <LinearProgress sx={{ mb: 2 }} />}

                        {questionAwnser?.items && questionAwnser.items.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {questionAwnser.items.map((element, index) => (
                                    <Accordion
                                        key={index}
                                        expanded={expanded === `panel-${element.id}`}
                                        onChange={handleChange(`panel-${element.id}`)}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', pr: 2 }}>
                                                <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                                                    {element?.question}
                                                </Typography>
                                                <Typography variant="caption" color="primary">
                                                    {element?.category}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                {element?.answer}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Nenhuma pergunta encontrada
                                </Typography>
                            </Box>
                        )}

                        {questionAwnser?.pageCount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={questionAwnser.pageCount}
                                    page={(paramsRequet.page || 0) + 1}
                                    onChange={(_, page) => {
                                        setParamsRequest({ ...paramsRequet, page: page - 1 });
                                    }}
                                    disabled={loadMoreData}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                />
                            </Box>
                        )}
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
